import { Registration, TrainingEventAttendee } from '@dtu/types';
import { Environment, TestAttempt } from './shared';
import { Values } from './type-utils';

export const DoceboCourseIdToExamId = {
  // Courses with ID 8-16 are valid for both STG & PROD
  8: 46943, // Associate
  10: 49318, // Administration Professional
  11: 55657, // Associate for Managed
  12: 55657, // Associate for Managed - Internal
  13: 46943, // Associate - Internal
  14: 49318, // Administration Professional - Internal
  15: 49319, // Implementation Professional - Internal
  16: 49319, // Implementation Professional
  // PROD only
  789: 55749, // Associate Pilot
  790: 55749, // Associate Pilot - Internal
  834: 55750, // Implementation Professional Pilot
  835: 55750, // Implementation Professional Pilot - Internal
  840: 55753, // Administration Professional Pilot
  841: 55753, // Administration Professional Pilot - Internal
};

export const DoceboCourseCodeToCourseId = {
  '46943': 8, // Associate
  '46943-emp': 13, // Associate - Internal
  '49318': 10, // Administration Professional
  '49318-emp': 14, // Administration Professional - Internal
  '49319': 16, // Implementation Professional
  '49319-emp': 15, // Implementation Professional - Internal
  '55657': 11, // Associate for Managed
  '55657-emp': 12, // Associate for Managed - Internal
  '55749': 789, // Associate Pilot
  '55749-emp': 790, // Associate Pilot - Internal
  '55750': 834, // Implementation Professional Pilot
  '55750-emp': 835, // Implementation Professional Pilot - Internal
  '55753': 840, // Administration Professional Pilot
  '55753-emp': 841, // Administration Professional Pilot - Internal
};

export const DoceboCourseEnrollmentStatus = {
  Subscribed: '0',
  InProgress: '1',
  Completed: '2',
};
export type DoceboCourseEnrollmentStatus = Values<typeof DoceboCourseEnrollmentStatus>;

export interface DoceboCourseEnrollment {
  data: {
    records: {
      id: string;
      name: string;
      status: DoceboCourseEnrollmentStatus;
    };
  };
}

export const DoceboLearningObjectUserStatus = {
  Subscribed: 'subscribed',
  InProgress: 'in progress',
  Completed: 'completed',
};
export type DoceboLearningObjectUserStatus = Values<typeof DoceboLearningObjectUserStatus>;

export const DoceboLearningObjectType = {
  Tincan: 'tincan',
};
export type DoceboLearningObjectType = Values<typeof DoceboLearningObjectType>;

export const DoceboExamModule = {
  Schedule: 'Schedule',
  View: 'View',
  Completion: 'Completion',
};
export type DoceboExamModule = Values<typeof DoceboExamModule>;

export const DoceboArchiveEnrollmentMode = {
  Reset: 'reset',
  Delete: 'delete',
};
export type DoceboArchiveEnrollmentMode = Values<typeof DoceboArchiveEnrollmentMode>;

export interface DoceboArchiveEnrollmentPayload {
  reset_tracking: boolean;
  enrollment_mode: DoceboArchiveEnrollmentMode;
}

export interface DoceboArchiveEnrollmentPayloadNormalized {
  resetTracking: DoceboArchiveEnrollmentPayload['reset_tracking'];
  enrollmentMode: DoceboArchiveEnrollmentPayload['enrollment_mode'];
}

export function normalizedArchiveEnrollmentPayloadToDoceboArchiveEnrollmentPayload(
  archiveEnrollmentPayload: DoceboArchiveEnrollmentPayloadNormalized,
): DoceboArchiveEnrollmentPayload {
  return {
    reset_tracking: archiveEnrollmentPayload.resetTracking,
    enrollment_mode: archiveEnrollmentPayload.enrollmentMode,
  };
}

export interface DoceboTrackingPayload {
  username: string;
  course_code: string;
  lo_name: string;
  lo_user_status: DoceboLearningObjectUserStatus;
  lo_type: DoceboLearningObjectType;
  lo_score?: number;
}

export interface DoceboTrackingPayloadNormalized {
  username: DoceboTrackingPayload['username'];
  courseCode: DoceboTrackingPayload['course_code'];
  loName: DoceboTrackingPayload['lo_name'];
  loUserStatus: DoceboTrackingPayload['lo_user_status'];
  loType: DoceboTrackingPayload['lo_type'];
  loScore?: DoceboTrackingPayload['lo_score'];
}

export function normalizedTrackingPayloadToDoceboTrackingPayload(trackingPayload: DoceboTrackingPayloadNormalized): DoceboTrackingPayload {
  const payload: DoceboTrackingPayload = {
    username: trackingPayload.username,
    course_code: trackingPayload.courseCode,
    lo_name: trackingPayload.loName,
    lo_user_status: trackingPayload.loUserStatus,
    lo_type: trackingPayload.loType,
  };

  if (trackingPayload.loScore !== undefined) {
    payload.lo_score = trackingPayload.loScore;
  }

  return payload;
}

export interface DoceboXApiQueryParams {
  course_code: string;
  course_id: string | string[];
  username: string;
  user_id: string;
  xapi_auth_code: string;
}

export interface DoceboAwardedCertificationsResponse {
  data: {
    items: DoceboCertificationActivity[];
  };
}

export interface DoceboCertificationActivity {
  expiring_at: string;
  certification: DoceboCertification;
}

export interface DoceboCertification {
  code: string;
  name: string;
}

export interface DoceboDashboardCertification {
  name: string;
  imageUrl: string;
  badgeUrl: string;
}

export interface DoceboClassroomDetails {
  userType: DoceboClassroomUserType;
  sessions: DoceboSession[] | null;
}

export interface DoceboExamDetails {
  name: string;
  loginUrl: string;
  imageUrl: string;
  score?: number;
  testAttempt?: Pick<TestAttempt, 'complete' | 'graded' | 'passed'>;
}

export interface DoceboSession {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  classicEventId: number;
}

export interface DoceboSessionAttendeeDetails {
  session: DoceboSession;
  registration: Registration;
  environment?: Environment;
  attendees?: TrainingEventAttendee[];
}

export const enum DoceboEventStatus {
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Complete = 'complete',
}

export const enum DoceboClassroomUserType {
  Student = 'student',
  Instructor = 'instructor',
}

export const DOCEBO_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export interface DoceboUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}
